import * as React from "react";
import * as XLSX from "xlsx";

interface Ledger {
  ledger: string;
}

interface Benefit {
  ledger: Ledger;
  remark?: string;
  amount: number;
  date: string;
  month: string;
  year: string;
  createdAt: string;
  updatedAt: string;
  _id: string;
  __v: number;
}

interface ExportButtonProps {
  data: Benefit[];
  selectedPeriod: string;
  className?: string;
  buttonText?: string;
  icon?: React.ReactNode;
}

const useBenefitExcelExport = (data: Benefit[], selectedPeriod: string) => {
  const filterBenefitsByPeriod = (benefits: Benefit[]) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    return benefits?.filter((benefit: Benefit) => {
      const benefitDate = new Date(benefit.date);

      switch (selectedPeriod) {
        case "day":
          return benefitDate.toDateString() === currentDate.toDateString();
        case "week":
          const weekAgo = new Date();
          weekAgo.setDate(currentDate.getDate() - 7);
          return benefitDate >= weekAgo && benefitDate <= currentDate;
        case "month":
          return (
            benefitDate.getMonth() === currentMonth &&
            benefitDate.getFullYear() === currentYear
          );
        case "year":
          return benefitDate.getFullYear() === currentYear;
        default:
          return true;
      }
    });
  };

  const calculateTotals = (benefits: Benefit[]) => {
    let total = 0;
    const totalsByCategory: { [key: string]: number } = {};

    benefits.forEach((benefit) => {
      const category = benefit.ledger.ledger;
      totalsByCategory[category] = (totalsByCategory[category] || 0) + benefit.amount;
      total += benefit.amount;
    });

    return { totalsByCategory, Total: total };
  };

  const exportToExcel = () => {
    if (!data?.length) return;

    const filteredData = filterBenefitsByPeriod(data);

    // Transform the data for Excel
    const excelData = filteredData.map((benefit: Benefit) => ({
      Date: new Date(benefit.date).toLocaleDateString(),
      Category: benefit.ledger.ledger,
      Description: benefit.remark || "",
      Amount: benefit.amount,
      Month: benefit.month,
      Year: benefit.year,
    }));

    // Calculate totals
    const totals = calculateTotals(filteredData);

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create the transactions worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Adjust column widths
    const colWidths = [
      { wch: 15 }, // Date
      { wch: 15 }, // Category
      { wch: 30 }, // Description
      { wch: 12 }, // Amount
      { wch: 10 }, // Month
      { wch: 8 },  // Year
    ];
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Benefits Inflow");

    // Create the summary worksheet
    const summaryData = [
      ["Category", "Total Amount"],
      ...Object.entries(totals.totalsByCategory),
      ["Total Inflows", totals.Total],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    // Generate filename based on selected period
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `benefit_report_${selectedPeriod}_${timestamp}.xlsx`;

    // Save the file
    XLSX.writeFile(wb, filename);
  };

  return { exportToExcel };
};

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  selectedPeriod,
  className = "flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium shadow-sm border",
  buttonText,
  icon,
}) => {
  const { exportToExcel } = useBenefitExcelExport(data, selectedPeriod);

  return (
    <button
      onClick={exportToExcel}
      className={className}
      disabled={!data?.length}
    >
      {icon}
      {buttonText ||
        `Export ${
          selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)
        } Report`}
    </button>
  );
};

export default ExportButton;
